import { AddJobDiscount, Charge, CreateCalendarEventInput, CreateChargeInput, CreateDiscountInput, CurrentBrandingQuery, Discount, DiscountsQueryVariables, FullJobFragment, FullProductFragment, JobDetailsV2Fragment, Job_ZoneWithParentAndTimezoneFragment, ListProductsForEstimatingQueryVariables, NewChargesInput, Price, Product, SetFieldValueInput, Tag, ZoneDir } from 'graphql.generated';
import { compact, filter, flatMap, includes, map, minBy } from 'lodash';

import { isEstimateArtifact } from '../artifacts/artifact.util';
import { FIELD_CONFIG, JobEventStatus, eventTypeInfoMapV2 } from '../global.constants';

import {
  ChargesUpdate,
  ChargeUpdateV2,
} from './job-create/job-interfaces';
import { validateLocationsForEventType } from './job-helpers';


export function addChargeSubtotals(charges: Charge[]): number {
  if (!charges || !charges.length) {
    return 0;
  }

  let total = 0;
  for (const charge of charges) {
    total += getChargeSubtotal(charge);
  }

  return total;
}

export function getChargeSubtotal(
  charge: Charge,
  quantity: number = charge.quantity,
  product: Product = charge.product
): number {
  if (charge.chargeSubTotal !== undefined) {
    return charge.chargeSubTotal;
  }

  if (charge.price) {
    return charge.price.amount * quantity;
  } else if (charge.amount) {
    return charge.amount * quantity;
  }

  if (!quantity || !product || !product?.prices) {
    return 0;
  }

  return product.prices[0].amount * quantity;
}

export function getDisabledStatus<K extends { locked?: boolean; invoiced?: boolean; type?: string; requireSignedEstimate?: boolean }>(
  event: K,
  job: JobDetailsV2Fragment,
  validateLocations = false,
): DisabledStatus {
  let disabledStatus = false;
  let disabledToolTip = '';
  let disabledWarning = '';

  if (job?.zone?.type !== 'area') {
    disabledStatus = true;
    disabledToolTip = 'Your job needs to be in an area to apply charges.';
    disabledWarning = disabledToolTip;
  }

  if (job?.closedAt) {
    disabledStatus = true;
    disabledToolTip = 'This job is closed and cannot be modified.';
    disabledWarning = disabledToolTip;
  }

  // if event type is unspecified, we'll assume moving
  if (validateLocations) {
    const locationValidation = validateLocationsForEventType(job?.locations, event?.type ?? 'moving');
    if (!locationValidation.isValid) {
      disabledStatus = true;
      disabledToolTip = `Job is missing required locations for this event type. ${locationValidation.errorMessage}`;
      disabledWarning = disabledToolTip;
    }
  }

  const hasSignedEstimate = (job?.artifacts || [])
    .filter((doc) => isEstimateArtifact(doc))
    .some((doc) => doc.metadata?.signedByCustomer);


  if (event?.requireSignedEstimate && !hasSignedEstimate) {
    disabledStatus = true;
    disabledToolTip = 'A signed estimate is required before booking';
    disabledWarning = disabledToolTip;
  }

  if (event?.locked) {
    disabledStatus = true;
    disabledToolTip = 'This event is locked.';
    disabledWarning = `This event takes place before the lock date, so it cannot be modified.
    Please create a new event if you need to charge the customer additional charges.`;
  }

  if (event?.invoiced) {
    disabledStatus = true;
    disabledToolTip = `This event has been invoiced and cannot be modified.
     Please void the invoice or create a new event if you need to charge the customer additional charges.`;
    disabledWarning = disabledToolTip;
  }

  return {
    disabledStatus,
    disabledToolTip,
    disabledWarning,
  };
}

export const handleQuantity = (input: number | boolean | null): number => {
  if (input === null) {
    return 0;
  } else if (typeof input === 'number') {
    return input;
  } else if (typeof input === 'boolean') {
    return input ? 1 : 0;
  }
  return 0;
};

export function trackChanges(
  changes: ChargesUpdate[],
  change: ChargeUpdateV2,
) {
  const updatedChanges = [...changes, change];
  return updatedChanges;
}

//when products are being selected in modal, keep only one latest amount
//when product was already submitted twice, store two separate records
export function trackAddingCharges(
  changes: ChargesUpdate[],
  change: ChargesUpdate
) {
  const updatedChanges = [
    change,
    ...changes.filter(
      (existingChange) =>
        existingChange.productId !== change.productId ||
        !(existingChange.submitted === false && change.submitted === false)
    ),
  ];

  const filteredCharged = updatedChanges.filter(c => !c.removed);

  return filteredCharged;
}

export function trackUpdatingDiscounts(
  changes: ChargesUpdate[],
  change: ChargesUpdate): ChargesUpdate[] {
    return [...changes, change];
}

export function trackDuplicatingEvents(
  changes: ChargesUpdate[],
  change: ChargesUpdate): ChargesUpdate[] {
    return [...changes, change];
}

export function trackUpdatingEventStatus(
  changes: ChargesUpdate[],
  change: ChargesUpdate): ChargesUpdate[] {
    //if event cancelled or removed, filter out all changes related to that event
    const filteredChanges = changes
      ?.filter(c => c?.eventId !== change?.eventId)
      ?.map(c => ({
        ...c,
        eventsWithNewOrder: c?.eventsWithNewOrder?.filter(e => e.eventId !== change?.eventId)
      }));
    return [...filteredChanges, change];
}

export function trackModifyingCharges(
  changes: ChargesUpdate[],
  change: ChargesUpdate
): ChargesUpdate[] {
  const existingChange = changes.find(existing => existing.chargeId === change.chargeId);

  const mergedChange = existingChange
    ? {
        ...existingChange,
        ...change,
      }
    : change;

  const updatedChanges = [
    mergedChange,
    ...changes.filter(existing => existing.chargeId !== change.chargeId),
  ];

  return updatedChanges;
}

export function trackEventsReordering(
  changes: ChargesUpdate[],
  change: ChargesUpdate
): ChargesUpdate[] {
  const filteredChanges = changes.filter(existing => existing.changeType !== 'events-reordered');

  const updatedChanges = [
    ...filteredChanges,
    change
  ];

  return updatedChanges;
}

interface DisabledStatus {
  disabledStatus: boolean;
  disabledToolTip: string;
  disabledWarning: string;
}

export const generateAddDiscountsInput = (chargesUpdates: ChargesUpdate[]) => {
  const newDiscountsToCreate = chargesUpdates
  ?.filter(u => u?.changeType === 'discount-added' && u?.discountInput?.singleUse)
  .reduce((acc, u) => {
    const { appliedId: _appliedId, appliedAt: _appliedAt, singleUse: _singleUse, ...restDiscountInput } = u?.discountInput || {};

    // Ensure the required properties are included in restDiscountInput
    if (!restDiscountInput.amount || !restDiscountInput.code || !restDiscountInput.discountType || !restDiscountInput.name) {
      throw new Error('Missing required discount fields');
    }

    // Get the eventId for the current update and add it to the accumulator
    if (!acc.eventsIds) acc.eventsIds = [];
    const currentEventId = u?.eventId;
    if (currentEventId && !acc.eventsIds.includes(currentEventId)) {
      acc.eventsIds.push(currentEventId);
    }

    // Update the discountInput
    acc.discountInput = {
      ...restDiscountInput,
    } as CreateDiscountInput;

    return acc;
  }, { eventsIds: [], discountInput: {} as CreateDiscountInput });

  const existingDiscountsToAdd: AddJobDiscount[] = chargesUpdates
    ?.filter(u => u?.changeType === 'discount-added' && !u?.discountInput?.singleUse)
    .map(u => {
      const discount = {
        discountId: u?.discountId,
        eventId: u?.eventId,
        ...(u?.discountInput?.customAmount !== undefined
          ? { customAmount: u.discountInput.customAmount as number }
          : {}),
      };

      return discount;
    });

  return {
    existingDiscountsToAdd,
    newDiscountsToCreate,
  };
};

export const generateRemoveEventsInput = (chargesUpdates: ChargesUpdate[]): string[] => {
  const eventsDeletedUpdates = chargesUpdates?.filter(
    update => update?.changeType === 'event-deleted'
  );

  const deletedEventsIds = eventsDeletedUpdates?.map(c => c?.eventId);

  return deletedEventsIds;
}

export const generateFieldsInput = (chargesUpdates: ChargesUpdate[]): SetFieldValueInput[] => {
  const inventoryUpdatedChanges = chargesUpdates?.filter(
    update => update?.changeType === 'inventory-updated'
  );

  if (!inventoryUpdatedChanges?.length) {
    return [];
  }

  const change = inventoryUpdatedChanges[inventoryUpdatedChanges.length - 1];

  return [{
    fieldName: FIELD_CONFIG.jobs.inventory.name,
    value: JSON.stringify(change.inventory),
  }];
};


export const generateCreateEventsInput = (chargesUpdates: ChargesUpdate[]): CreateCalendarEventInput[] => {
  const eventAddedUpdatesChanges = chargesUpdates?.filter(
    update => update?.changeType === 'event-added'
  );

  const eventDuplicatedUpdatesChanges = chargesUpdates?.filter(
    update => update?.changeType === 'event-duplicated'
  );

  const createEventsInput = eventAddedUpdatesChanges?.map(c => {
    const { jobId: _jobId, ...restOfEventInput } = c.eventInput || {};
    return {
      id: c.eventId,
      ...restOfEventInput,
    };
  });

  const createDuplicatedEventsInput = eventDuplicatedUpdatesChanges?.map(c => {;
    return {
      id: c.eventInput?.id,
      title: c?.eventInput?.title,
      type: c?.eventInput?.type,
      sequentialOrder: c?.eventInput?.sequentialOrder,
      status: c?.eventInput?.status,
    };
  });

  const allEventsInput = [...createEventsInput, ...createDuplicatedEventsInput];

  return allEventsInput as CreateCalendarEventInput[];
};

export const generateEditEventsInput = (chargesUpdates: ChargesUpdate[]) => {
  const eventsEdited = chargesUpdates?.filter(
    update => update?.changeType === 'events-reordered' || update?.changeType === 'event-cancelled'
  );

  const edits = eventsEdited?.reduce((acc, update) => {
    if (update.changeType === 'events-reordered' && update.eventsWithNewOrder) {
      const reorderedEdits = update.eventsWithNewOrder.map(e => ({
        id: e.eventId,
        edit: { sequentialOrder: e.newOrder },
      }));
      acc.push(...reorderedEdits);
    } else if (update.changeType === 'event-cancelled' && update.eventId) {
      acc.push({
        id: update.eventId,
        edit: { status: 'cancelled' },
      });
    }
    return acc;
  }, [] as { id: string; edit: { sequentialOrder?: number; status?: string } }[]);

  return { edits };
};

//not created yet charges with latest updates for them

export const generateRemoveChargesInput = (
  chargesUpdates: ChargesUpdate[],
  savedChargesIds: string[]
): string[] =>
  chargesUpdates
    ?.filter(u => u.removed && savedChargesIds.includes(u.chargeId))
    .map(u => u.chargeId) || [];

export const generateRemoveDiscountsInput = (chargesUpdates: ChargesUpdate[]): string[] =>
  chargesUpdates
    ?.filter(u => u.changeType === 'discount-removed')
    .map(u => u.discountInput?.appliedId as string) || [];

export const generateNewChargesInput = (
  userId: string,
  products: FullProductFragment[],
  changes: ChargesUpdate[],
  currency: string,
): NewChargesInput =>
  {
    const chargesToAdd = changes
    .filter(c => !c?.removed)
    .map(chargeUpdate => {
        const matchingProduct = products.find(product => product.id === chargeUpdate.productId);

        return {
          id: chargeUpdate?.chargeId,
          eventId: chargeUpdate.eventId,
          quantity: chargeUpdate.quantity,
          amount: chargeUpdate.amount ?? undefined,
          ...(chargeUpdate?.productId && { productId: chargeUpdate.productId }),
          ...(!chargeUpdate?.productId && chargeUpdate?.productName && { productName: chargeUpdate.productName }),
          attributes: matchingProduct?.attributes || [],
          ...( chargeUpdate?.order && { order: chargeUpdate?.order }),
          ...( chargeUpdate?.taxIds && { taxIds: chargeUpdate?.taxIds }),
          //add currency to the query input only when create custom charge, not connected to product
          ...(chargeUpdate.productName !== undefined ? { currency: currency } : {}),
        } as CreateChargeInput;
      });

      return  {
        charges: chargesToAdd,
        userId: userId,
      };
}

export function removeUnsavedChangesForCancelledRemovedEvent(
  chargesUpdates: ChargesUpdate[],
  eventId: string) {
    const filteredChanges = chargesUpdates?.filter(change => change?.eventId !== eventId);

    return filteredChanges;
}

export function calculateChargeOffset(
  eventId: string,
  eventType: string,
  allEvents: FullJobFragment['events']
) {
  let eventTypeOffset: number;
  if (eventTypeInfoMapV2[eventType]) {
    eventTypeOffset = eventTypeInfoMapV2[eventType].order;
  } else {
    eventTypeOffset = Object.keys(eventTypeInfoMapV2).length + 1;
  }

  const eventTypeBase = 1000;
  eventTypeOffset *= eventTypeBase;
  let offset = eventTypeOffset;

  const sameTypeEvents = allEvents?.filter((e) => e.type === eventType);
  sameTypeEvents.sort((a, b) => {
    const aValue = a.start || a.createdAt;
    const bValue = b.start || b.createdAt;
    return aValue - bValue;
  });

  const eventBase = 100;
  const event = sameTypeEvents.find((e) => e.id === eventId);
  const eventOffset = (sameTypeEvents.indexOf(event) + 1) * eventBase;
  offset += eventOffset + 50;

  return offset;
}

export function setOrderToIndexV2(array: any[], offset = 0) {
  return array.map((item, index) => ({
      ...item,
      order: index + offset
  }));
};

export function getDiscountFromUpdates(
  matchingDiscount: Partial<Discount>,
  change: ChargesUpdate,
  singleUse: boolean) {
  if (matchingDiscount && !singleUse) {
    return matchingDiscount;
  } else if (!matchingDiscount && singleUse) {
    return {
      ...change?.discountInput,
    };
  } else {
    return {};
  }
}

export const deletableEventStatuses: JobEventStatus[] = [
  'required',
  'cancelled',
];

export function isJobTaxExempt(jobTags: Partial<Tag>[]): boolean {
  return jobTags.some(tag => tag.attributes?.includes('tax-exempt'));
}

export const generateListAvailableProductsQueryVariables = (zoneId: string) => {
  const listProductQueryVariables: ListProductsForEstimatingQueryVariables = {
    limit: 200,
    skip: 0,
    filter: {
      hasActivePrice: true,
      isActive: true,
      zoneDir: ZoneDir.Any,
    },
    sort: 'name:ASC',
    pricesFilter: {
      isActive: true,
    },
  };

  const headers = {
    context: {
      headers: {
        'x-zone': zoneId,
      },
    },
  };

  return {
    listProductQueryVariables,
    headers
  }
}

export const generateListDiscountsQueryVariables = (zoneId: string) => {
  const listDiscountsQueryVariables: DiscountsQueryVariables = {
    filter: {
      applyable: true,
      singleUse: false,
    },
    limit: -1,
  };

  const headers = {
    context: {
      headers: {
        'x-zone': zoneId,
      },
    },
  };

  return {
    listDiscountsQueryVariables,
    headers
  }
}

export function filterOutProductsWithInactivePrices(
  products: CurrentBrandingQuery['products']['products'],
): CurrentBrandingQuery['products']['products'] {
  return products.filter((p) => p.prices?.length && p.prices.find((price) => price.active));
}

export function sortProductsByCategory(
  products: CurrentBrandingQuery['products']['products'],
): CurrentBrandingQuery['products']['products'] {
  return products.sort((prodA, prodB) => {
    if (!prodA.category) return 1;
    if (!prodB.category) return -1;
    return prodA.category.localeCompare(prodB.category);
  });
}

export function getProductsWithClosestPricesByJobZone(
  products: FullProductFragment[],
  jobZone: Job_ZoneWithParentAndTimezoneFragment['zone']
): FullProductFragment[] {
  // Extract jobZone and all parent zone IDs
  const jobZoneAndAllParentsIds = compact([
    jobZone?.id,
    ...flatMap(jobZone?.parents, parent => parent?.id)
  ]);

  // Filter out prices from zones lower than job zone
  const productsWithGTEPrices = map(products, (product: Product) => ({
    ...product,
    prices: filter(product.prices, (price: Price) => includes(jobZoneAndAllParentsIds, price.zone.id))
  })).filter(product => product.prices.length > 0);

  // Assign proximity and keep only the lowest proximity price
  const productsWithLowestProximityPrice = map(productsWithGTEPrices, (product: Product) => ({
    ...product,
    prices: [minBy(product.prices, price => {
      const parentIndex = jobZoneAndAllParentsIds.findIndex(id => id === price.zone.id);
      return parentIndex !== -1 ? parentIndex : Infinity;
    })]
  })).filter(product => product.prices.length > 0);

  return productsWithLowestProximityPrice;
}
